import { faClose, faPenToSquare } from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import type { TimeClockAdjustment } from "../../../Objects/TimeClock/TimeClockAdjustment"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface TimeClockAdjustmentReviewPopupProps {
  adjustment: TimeClockAdjustment
  isLoading: boolean
  onClose: () => void
  onReview: (isApproved: boolean, reviewNotes?: string) => Promise<boolean>
}

export const TimeClockAdjustmentReviewPopup: React.FC<TimeClockAdjustmentReviewPopupProps> = ({
  adjustment,
  isLoading,
  onClose,
  onReview,
}) => {
  const { t } = useTranslation(["timeClock", "helpPopup"])

  const [reviewNotes, setReviewNotes] = useState("")

  const isPending = adjustment.status === "Pending"

  const handleReview = async (isApproved: boolean) => {
    const reviewed = await onReview(isApproved, reviewNotes.trim() || undefined)

    if (reviewed) onClose()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={adjustment.employeeName}
              icon={faPenToSquare}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-adjustment-review"
                    title={t("helpPopup:global.timeClockAdjustmentReview.title")}
                    description={t("helpPopup:global.timeClockAdjustmentReview.description")}
                    bullets={
                      t("helpPopup:global.timeClockAdjustmentReview.bullets", { returnObjects: true }) as string[]
                    }
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockAdjustmentReviewPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="flex flex-col gap-3 p-2">
              <Animate type={AnimationTypes.slideUpIn} duration="1.6s">
                <span className="w-fit rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900">
                  {t(`common.adjustment_${adjustment.status.toLowerCase()}`)}
                </span>
              </Animate>

              <Animate type={AnimationTypes.slideUpIn} duration="1.7s">
                <p className="rounded-xl border border-purple-200 bg-purple-50 p-3 text-sm font-medium text-purple-900">
                  {adjustment.reason}
                </p>
              </Animate>

              <InputField
                type="text"
                value={reviewNotes}
                maxLength={500}
                disable={!isPending}
                placeholder={t("timeClock:management.review_notes")}
                onChange={(event) => setReviewNotes(event.target.value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockAdjustmentReviewPopup.input.reviewNotes"
              />

              <div className={defaultStyle.divisionBar}></div>

              <Animate type={AnimationTypes.slideUpIn} duration="1.8s">
                <div className={defaultStyle.buttonsController}>
                  <Button
                    typeButton="button"
                    label={t("timeClock:management.approve")}
                    size="small"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    disabled={isLoading || !isPending}
                    onClick={() => void handleReview(true)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockAdjustmentReviewPopup.button.approve"
                  />

                  <Button
                    typeButton="button"
                    label={t("timeClock:management.reject")}
                    size="small"
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    disabled={isLoading || !isPending}
                    onClick={() => void handleReview(false)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockAdjustmentReviewPopup.button.reject"
                  />
                </div>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
